// Action registry — maps a proposal's action_type to the existing role-gated
// REST call that executes it (plan 2026-07-28 platform assistant, step 0.3).
//
// The agent never mutates: a confirm click claims the proposal (CAS), then
// runs the executor registered here under the user's own JWT, then records
// the outcome. Registering an action is USUALLY the only code change a new
// proposal type needs on the frontend — the card renders itself from the
// proposal's generic `title`/`summary`/`preview`.
//
// That stops holding when the generic preview cannot show what the user is
// being asked to confirm. The business-context types (plan 2026-08-09) are the
// first such case: a profile diff has to show unset current values, which the
// preview filter drops, and a proposed document has to show its whole body.
// Those render a dedicated body in `ActionProposalCard.CONTEXT_BODY`. Adding
// one is a deliberate decision, not the default path.
import type { ActionExecutionResult } from "@/types/actionProposals";

export type ActionExecutor = (
  params: Record<string, unknown>
) => Promise<ActionExecutionResult>;

/**
 * Sentinel an executor throws when the user cancelled an interactive handoff
 * (e.g. closed the connect-source wizard without finishing). `ActionProposalCard`
 * catches this specifically and returns the card to actionable (release), rather
 * than marking it failed. Any other thrown value is treated as a real failure.
 */
export const ACTION_CANCELLED = Symbol("ACTION_CANCELLED");

/**
 * A failure whose message is written FOR the user and must reach them intact.
 *
 * The card falls back to a generic message for anything it cannot vouch for,
 * which is right for raw exceptions but wrong for a refusal the executor
 * decided on itself — "this was edited since the assistant proposed it" tells
 * the user what happened and what to do; "the action could not be completed"
 * tells them nothing. Throw this when the executor, not the API, is refusing.
 */
export class ActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ActionError";
  }
}

const registry = new Map<string, ActionExecutor>();

export function registerAction(actionType: string, executor: ActionExecutor): void {
  if (registry.has(actionType)) {
    // Double registration is a wiring bug — fail loudly in dev, last-wins in prod.
    if (import.meta.env.DEV) {
      throw new Error(`Action executor already registered for "${actionType}"`);
    }
  }
  registry.set(actionType, executor);
}

export function getActionExecutor(actionType: string): ActionExecutor | undefined {
  return registry.get(actionType);
}
